import {
  isFlagProviderConfigured,
  useAppliedFlagTargetingKey,
  useIsFlagProviderFailed,
  useIsIntegrationsMarketplaceHidden,
} from '@/features/flags';
import { useQueryUser } from '@heidi/workspace/data';
import { useEffect } from 'react';

/**
 * ANZ-2490 marketplace discovery visibility.
 * - `pending` — omit UI; do not redirect (flag client has not applied this user yet).
 * - `hidden` — hide ON for the signed-in identity.
 * - `visible` — hide OFF, or fail-open (no provider / provider ERROR).
 */
export type IntegrationsMarketplaceVisibility = 'pending' | 'visible' | 'hidden';

export const useIntegrationsMarketplaceVisibilityGate =
  (): IntegrationsMarketplaceVisibility => {
    const providerFailed = useIsFlagProviderFailed();
    const isHidden = useIsIntegrationsMarketplaceHidden();
    const { data: userRefId } = useQueryUser((u) => u?.user_ref_id);
    const appliedTargetingKey = useAppliedFlagTargetingKey();

    // Local / builds that never register a provider stay NOT_READY forever —
    // same fail-open as `useIsFlagProviderSettled`.
    if (!isFlagProviderConfigured() || providerFailed) {
      return 'visible';
    }
    if (!userRefId || appliedTargetingKey !== userRefId) {
      return 'pending';
    }
    return isHidden ? 'hidden' : 'visible';
  };

/** Gate + replace-nav to Account when visibility is `hidden`. */
export const useMarketplaceVisibilityOrRedirect = (
  replace: (path: string) => unknown
): IntegrationsMarketplaceVisibility => {
  const visibility = useIntegrationsMarketplaceVisibilityGate();
  useEffect(() => {
    if (visibility === 'hidden') {
      void replace('/settings/account');
    }
  }, [visibility, replace]);
  return visibility;
};
